/**
 * mm-sticky-atc.js
 * ----------------
 * Floating (sticky) Add to Cart bar for product templates that do NOT run
 * mix-match.js — currently product.mix-match-sale.json and
 * product.mix-match-style.json, both of which render via
 * snippets/mix-match-static-title.liquid.
 *
 * Why this exists as a separate file:
 *   The sticky bar on mix-match pages is built by MixMatch.initStickyATC()
 *   in assets/mix-match.js. That class returns early in its constructor when
 *   there is no `mix-match-config-<id>` script tag (mix-match.js ~line 43),
 *   which the static-title templates deliberately don't emit. Rather than
 *   refactor mix-match.js — which runs on the main PDPs — this is a
 *   standalone port of just the sticky-bar behaviour.
 *
 *   Behaviour is intentionally identical to the mix-match version: same
 *   markup, same classes, same thresholds. All styling already lives in
 *   mix-match.css (.mm-sticky-atc*), which the static snippet loads.
 *
 * If mix-match.js is ever refactored, this is the obvious thing to share.
 */
(function () {
  'use strict';

  var SECTION = '[data-section-type="product-template"]';

  function init() {
    // Mobile only — same breakpoint as mix-match.js.
    if (window.innerWidth > 767) return;

    // Never double-create. Guards against this script and mix-match.js both
    // running on some future template that renders both blocks.
    if (document.querySelector('.mm-sticky-atc')) return;

    var section = document.querySelector(SECTION);
    if (!section) return;

    var realATC = section.querySelector('.product__submit__holder');
    if (!realATC) return;

    var stickyBar = document.createElement('div');
    stickyBar.className = 'mm-sticky-atc';

    var stickyBtn = document.createElement('button');
    stickyBtn.type = 'button';
    stickyBtn.className = 'btn btn--large product__submit__add mm-sticky-atc__btn';
    stickyBtn.textContent = 'Add to Cart';

    // Mirror the page's own sub-line rather than hardcoding a second copy.
    // The annual-sale template swaps this for the reviews line (and adds the
    // --reviews modifier) in snippets/product.liquid, so copying it here keeps
    // the floating button correct on every template with no branching, and
    // there is only one place to change the copy.
    // Prefer the data attribute: the GBP store's span carries only
    // .size-subheading on non-sale templates, so a class selector misses it.
    var pageSub = section.querySelector('[data-atc-subheading]')
      || section.querySelector('.atc-subheading');
    var stickySub = document.createElement('span');
    stickySub.className = 'atc-subheading' +
      (pageSub && pageSub.classList.contains('atc-subheading--reviews') ? ' atc-subheading--reviews' : '');
    // textContent, not innerHTML — the source is merchant-editable copy.
    // No hardcoded fallback: if the merchant clears the setting,
    // product.liquid renders no span, and the floating button drops its
    // sub-line too rather than showing stale copy.
    stickySub.textContent = pageSub ? pageSub.textContent.trim() : '';

    stickyBar.appendChild(stickyBtn);
    if (stickySub.textContent) stickyBar.appendChild(stickySub);
    document.body.appendChild(stickyBar);

    // Mirror the real button's label, disabled and sold-out state.
    function syncStickyBtn() {
      var realBtn = section.querySelector('.product__submit__add');
      if (!realBtn) return;

      stickyBtn.textContent = realBtn.textContent.trim();
      stickyBtn.disabled = realBtn.disabled;

      var isSoldOut = !!realBtn.closest('.variant--soldout') || !!realBtn.closest('.variant--unavailabe');
      stickyBtn.classList.toggle('mm-sticky-atc__btn--sold-out', isSoldOut);

      // Class-based styling only — never copy computed styles.
      stickyBtn.style.removeProperty('background-color');
      stickyBtn.style.removeProperty('color');
    }

    // Delegate to the real ATC so the theme's native add-to-cart flow runs
    // (and the cart drawer re-renders itself). Never re-implement the add.
    stickyBtn.addEventListener('click', function () {
      var realBtn = section.querySelector('.product__submit__add');
      if (realBtn && !realBtn.disabled) realBtn.click();
    });

    syncStickyBtn();

    // Theme JS applies variant--soldout / disabled in sequence, so re-sync
    // on a tick as well as immediately.
    var formWrapper = section.querySelector('[data-form-wrapper]') || section;
    var mo = new MutationObserver(function () {
      syncStickyBtn();
      setTimeout(syncStickyBtn, 80);
    });
    mo.observe(formWrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled'],
      characterData: true
    });

    // Show once scrolled past 25% of the viewport AND the real ATC is off-screen.
    var atcVisible = false;
    var scrolledPast = false;

    function update() {
      stickyBar.classList.toggle('mm-sticky-atc--visible', scrolledPast && !atcVisible);
    }

    var io = new IntersectionObserver(function (entries) {
      atcVisible = entries[0].isIntersecting;
      update();
    }, { threshold: 1.0 });
    io.observe(realATC);

    function onScroll() {
      scrolledPast = window.scrollY > window.innerHeight * 0.25;
      update();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
